// Interactie van de publieke site. Alles werkt ook zonder JavaScript leesbaar; dit voegt enkel gedrag toe.

import "./site.css";
import { lenis } from "./motion.ts";
import { bewaarHerkomst, leadAttributie } from "./herkomst.ts";
import { meetContactKliks } from "./meting.ts";
import { koppelToestemming } from "./toestemming.ts";

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// Herkomst eerst, zodat de calculator op dezelfde pagina ze al kan meesturen.
try {
  bewaarHerkomst(sessionStorage, new URL(location.href), document.referrer);
} catch {
  // sessionStorage zelf kan in sommige browsers al gooien.
}
// Vóór de calculator: die leest bij elk event of er toestemming is.
koppelToestemming(window);
meetContactKliks(document, () => (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag);

const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) => Array.from(root.querySelectorAll<T>(sel));

// Mobiel menu
for (const toggle of $$<HTMLButtonElement>("[data-menu-toggle]")) {
  const menu = document.getElementById(toggle.getAttribute("aria-controls") ?? "");
  toggle.addEventListener("click", () => {
    const open = toggle.getAttribute("aria-expanded") !== "true";
    toggle.setAttribute("aria-expanded", String(open));
    toggle.textContent = open ? "Sluiten" : "Menu";
    menu?.setAttribute("data-open", String(open));
    document.body.style.overflow = open ? "hidden" : "";
    if (open) lenis?.stop(); else lenis?.start();
  });
}

// Hero: dezelfde keuken in verschillende folies
for (const stage of $$("[data-hero-slides]")) {
  const slides = $$<HTMLImageElement>(".hero__slide, [data-hero-slide]", stage);
  if (slides.length < 2 || reducedMotion) continue;
  let index = 0;
  setInterval(() => {
    const next = (index + 1) % slides.length;
    slides[next].loading = "eager";
    slides[index].dataset.active = "false";
    slides[next].dataset.active = "true";
    index = next;
  }, 2600);
}

// Carrousels
function railStep(rail: HTMLElement) {
  const first = rail.firstElementChild as HTMLElement | null;
  const gap = parseFloat(getComputedStyle(rail).columnGap || "12") || 12;
  return first ? first.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
}
for (const btn of $$<HTMLButtonElement>("[data-rail-prev],[data-rail-next]")) {
  const id = btn.dataset.railPrev ?? btn.dataset.railNext!;
  const rail = document.getElementById(id);
  if (!rail) continue;
  const dir = btn.dataset.railPrev ? -1 : 1;
  btn.addEventListener("click", () => rail.scrollBy({ left: dir * railStep(rail), behavior: reducedMotion ? "auto" : "smooth" }));
  const sync = () => {
    const max = rail.scrollWidth - rail.clientWidth - 2;
    btn.disabled = dir < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= max;
  };
  rail.addEventListener("scroll", sync, { passive: true });
  window.addEventListener("resize", sync);
  sync();
}

// Voor/na
for (const ba of $$("[data-ba]")) {
  const range = ba.querySelector<HTMLInputElement>("[data-ba-range]")!;
  const buttons = $$<HTMLButtonElement>("[data-ba-show]", ba);
  const set = (pos: number) => {
    ba.style.setProperty("--pos", `${pos}%`);
    range.value = String(pos);
    buttons.forEach((b) => b.setAttribute("aria-pressed", String((b.dataset.baShow === "before") === pos < 50)));
  };
  range.addEventListener("input", () => set(Number(range.value)));
  buttons.forEach((b) => b.addEventListener("click", () => set(b.dataset.baShow === "before" ? 0 : 100)));
  set(100);
}

// Populaire combinaties
for (const root of $$("[data-combos]")) {
  const photo = root.querySelector<HTMLImageElement>("[data-combo-photo]")!;
  const label = root.querySelector<HTMLElement>("[data-combo-label]")!;
  const text = root.querySelector<HTMLElement>("[data-combo-text]")!;
  const swatches = $$<HTMLButtonElement>(".swatch, [data-combo-swatch]", root);
  const show = (s: HTMLButtonElement) => {
      swatches.forEach((x) => x.setAttribute("aria-pressed", String(x === s)));
      photo.style.opacity = "0";
      const next = new Image();
      next.src = s.dataset.comboPhotoSrc!;
      next.decode().catch(() => undefined).finally(() => {
        photo.src = next.src;
        photo.alt = s.dataset.comboPhotoAlt ?? "";
        photo.style.opacity = "1";
      });
      label.textContent = s.dataset.comboName ?? "";
      text.textContent = s.dataset.comboDescription ?? "";
  };
  // Automatisch wisselen met een voortgangsbalkje (zoals op Framer), enkel in beeld en zonder reduced motion.
  const autoplay = root.dataset.combosAutoplay === "true" && !reducedMotion;
  const INTERVAL = 4000;
  let timer = 0;
  const restart = () => {
    if (!autoplay) return;
    window.clearTimeout(timer);
    root.dataset.running = "false";
    void root.offsetWidth; // voortgangsanimatie opnieuw starten
    root.dataset.running = "true";
    timer = window.setTimeout(() => {
      const current = swatches.findIndex((x) => x.getAttribute("aria-pressed") === "true");
      show(swatches[(current + 1) % swatches.length]);
      restart();
    }, INTERVAL);
  };
  swatches.forEach((s) => s.addEventListener("click", () => { show(s); restart(); }));
  if (autoplay) {
    root.style.setProperty("--ds-combo-interval", `${INTERVAL}ms`);
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) restart();
      else { window.clearTimeout(timer); root.dataset.running = "false"; }
    }, { threshold: 0.3 }).observe(root);
  }
}

// Video (design system): speelt pas na een klik op de knop, met geluid.
for (const frame of $$("[data-video]")) {
  const play = frame.querySelector<HTMLButtonElement>("[data-video-play]");
  if (!play) continue;
  const video = frame.querySelector("video")!;
  play.addEventListener("click", () => {
    frame.dataset.playing = "true";
    video.muted = false;
    video.controls = true;
    video.preload = "auto";
    void video.play().catch(() => undefined);
  });
}

// Video (oude pagina's): speelt alleen als hij in beeld is
for (const frame of $$("[data-video]")) {
  if (frame.querySelector("[data-video-play]")) continue;
  const video = frame.querySelector("video")!;
  const toggle = frame.querySelector<HTMLButtonElement>("[data-video-toggle]")!;
  let userPaused = reducedMotion;
  const render = () => {
    toggle.setAttribute("aria-label", video.paused ? "Video afspelen" : "Video pauzeren");
    toggle.innerHTML = video.paused
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><polygon points="6 3 20 12 6 21 6 3"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><rect x="14" y="4" width="4" height="16" rx="1"/><rect x="6" y="4" width="4" height="16" rx="1"/></svg>';
  };
  toggle.addEventListener("click", () => {
    userPaused = !video.paused;
    if (video.paused) void video.play(); else video.pause();
  });
  video.addEventListener("play", render);
  video.addEventListener("pause", render);
  new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting && !userPaused) { video.preload = "auto"; void video.play().catch(() => undefined); }
      else if (!e.isIntersecting) video.pause();
    }
  }, { threshold: 0.3 }).observe(video);
  render();
}

function setStatus(form: HTMLFormElement, kind: "ok" | "error" | "", message: string) {
  const status = form.querySelector<HTMLElement>("[data-form-status]");
  if (!status) return;
  status.dataset.kind = kind;
  status.textContent = message;
}

function herkomstVoorLead() {
  try {
    return leadAttributie(sessionStorage, new URL(location.href));
  } catch {
    return { landingPage: location.origin + location.pathname };
  }
}

// Contactformulier → Wrappie HQ
for (const form of $$<HTMLFormElement>("[data-contact-form]")) {
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
    if (!data.name?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email ?? "") || !data.message?.trim()) {
      setStatus(form, "error", "Vul je naam, een geldig e-mailadres en je bericht in.");
      return;
    }
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    button.disabled = true;
    setStatus(form, "", "Bezig met versturen…");
    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...data, source: "website-contact", pageUrl: location.href, attribution: herkomstVoorLead() }),
      });
      if (!response.ok) throw new Error(String(response.status));
      form.reset();
      setStatus(form, "ok", "Bedankt! We hebben je bericht goed ontvangen en antwoorden zo snel mogelijk.");
    } catch {
      setStatus(form, "error", "Versturen lukte niet. Probeer het opnieuw, of mail ons op contact@wrappie.be.");
    } finally {
      button.disabled = false;
    }
  });
}

// Postcodecheck van /prijsindicatie → calculator.
// Zelfde contract als de vroegere Framer-component: sessionStorage "wrappie:postcode-handoff:v1" = { postcode, checkedAt }.
const HANDOFF_KEY = "wrappie:postcode-handoff:v1";
const FORWARDED_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "gclid"];
for (const form of $$<HTMLFormElement>("[data-postcode-form]")) {
  const input = form.querySelector<HTMLInputElement>("input[name=postcode]")!;
  let busy = false;
  let lastChecked = "";
  const run = async () => {
    const postcode = input.value.trim();
    if (busy) return;
    if (!/^\d{4}$/.test(postcode)) {
      setStatus(form, "error", "Vul een geldige Belgische postcode met 4 cijfers in.");
      input.focus();
      return;
    }
    busy = true;
    lastChecked = postcode;
    setStatus(form, "", "Even controleren…");
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(form.dataset.endpoint!, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postcode }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("postcode_check_failed");
      const result = await response.json();
      if (!result || typeof result !== "object" || Array.isArray(result) || typeof result.insideWorkArea !== "boolean") throw new Error("invalid");
      if (!result.insideWorkArea) {
        setStatus(form, "error", "Momenteel komen we nog niet tot in jouw regio.");
        return;
      }
      sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ postcode, checkedAt: Date.now() }));
      const target = new URL("/keuken-wrappen-aanvragen", location.origin);
      const current = new URLSearchParams(location.search);
      for (const key of FORWARDED_PARAMS) {
        const value = current.get(key);
        if (value) target.searchParams.set(key, value);
      }
      location.assign(target.pathname + target.search);
    } catch (error) {
      setStatus(form, "error", (error as Error).name === "AbortError" ? "De controle duurt te lang. Probeer het opnieuw." : "De regio kon niet worden gecontroleerd. Probeer het opnieuw.");
    } finally {
      clearTimeout(timer);
      busy = false;
    }
  };
  form.addEventListener("submit", (e) => { e.preventDefault(); void run(); });
  input.addEventListener("input", () => {
    input.value = input.value.replace(/\D/g, "").slice(0, 4);
    if (/^\d{4}$/.test(input.value) && input.value !== lastChecked) void run();
  });
}
